import type { PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { bedBankOfferMap } from './bedbankOfferMap'
import { hotelOfferMap } from './hotelOfferMap'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'

export function hotelListingOfferMap(summary: PublicOfferV2.LeHotelOfferSummary, region: string, markupSource?: ChannelMarkupSource): App.HotelOfferSummary | undefined {
  // A member-ranked lead alone cannot establish the cheapest public tactical card price.
  if (summary.type === OFFER_TYPE_ALWAYS_ON && summary.hasTactical) return
  const lead = summary.leadOption
  if (!lead?.option || !lead.package || !lead.ratePlan || !lead.roomRate || !lead.roomType) return
  // The published 2.5.1 contract predates offer-wide upgrades; preserve parity via fallback.
  const loyalty = 'loyalty' in summary ? summary.loyalty : undefined
  if (!loyalty || typeof loyalty !== 'object' ||
    !('hasPackageTypeUpgrades' in loyalty) || typeof loyalty.hasPackageTypeUpgrades !== 'boolean' ||
    !('hasRoomTypeUpgrades' in loyalty) || typeof loyalty.hasRoomTypeUpgrades !== 'boolean') return

  // Reuse the existing pricing/inclusion mapper with only the selected package graph.
  // Empty detail-only fields must never be stored as a full offer: navigation fetches those anew.
  const tileOffer: PublicOfferV2.LeHotelOffer = {
    ...summary,
    copy: { facilities: '', finePrint: '', gettingThere: '', highlights: null, whatWeLike: '' },
    insurance: { countries: [] },
    experiencesInFlow: false,
    showOnlyExperiencesCurated: false,
    numberOfDateChanges: null,
    preheader: '',
    subjectLine: '',
    packageUpgradesAllowed: false,
    paymentScheduleTemplates: [],
    dynamicPaymentScheduleTemplates: [],
    lowestOption: lead,
    lowestOptions: [lead.option],
    occupancyLowestOptions: [],
    options: [lead.option],
    packages: { [lead.option.fkPackageId]: lead.package },
    roomTypes: { [lead.option.fkRoomTypeId]: lead.roomType },
    roomRates: { [lead.option.fkRoomRateId]: lead.roomRate },
    ratePlans: { [lead.option.fkRatePlanId]: lead.ratePlan },
    stats: { ...summary.stats, popularRoomsByOccupancy: {} },
  }
  const mapped = hotelOfferMap(tileOffer, region, markupSource)
  // An empty lead cannot establish whether another package supplies flash-card inclusions.
  if (summary.type === 'hotel' && !mapped.tileInclusions?.length && !mapped.lowestPricePackage?.inclusions?.length) return
  return {
    ...mapped,
    packages: [],
    loyalty: {
      hasPackageTypeUpgrades: loyalty.hasPackageTypeUpgrades,
      hasRoomTypeUpgrades: loyalty.hasRoomTypeUpgrades,
    },
    hasBuyNowBookLaterPackages: summary.type === 'hotel' && summary.hasBuyNowBookLaterPackages,
    hasHiddenCancellationPolicy: summary.hasHiddenCancellationPolicy,
    hasTactical: summary.hasTactical,
    luxPlus: { ...mapped.luxPlus, hasMemberPrices: summary.luxPlus.hasMemberPrices },
    minDuration: summary.availableDurations[0] ?? lead.duration,
    availableDurations: summary.availableDurations,
    tileDurationLabel: summary.availableDurations.includes(1) ? '' : mapped.durationLabel,
  }
}

export function bedbankListingOfferMap(summary: PublicOfferV2.BedbankOfferSummary, markupSource?: ChannelMarkupSource): App.BedbankOfferSummary | undefined {
  if (!('tagline' in summary.copy)) return
  const tagline = summary.copy.tagline
  if (tagline !== null && typeof tagline !== 'string') return
  return bedBankOfferMap({
    ...summary,
    copy: { ...summary.copy, tagline: tagline ?? undefined },
    aboutThisProperty: [],
    amenityGroups: [],
    packages: [],
    paymentMethods: [],
    videos: [],
    propertyFinePrint: { checkIn: {}, checkOut: {} },
    stats: { ...summary.stats, popularRoomsByOccupancy: {} },
  }, undefined, markupSource)
}
